import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Comment, TimelineItemWithCommentCount } from "@travel/shared";
import { api } from "../../api/client";
import { useComments } from "../../hooks/useComments";
import { useLongPress } from "../../hooks/useLongPress";
import { useAuth } from "../../contexts/AuthContext";
import { BottomSheet } from "../common/BottomSheet";
import { Button } from "../common/Button";
import { ConfirmDialog } from "../common/ConfirmDialog";

interface Props {
  item: TimelineItemWithCommentCount | null;
  onClose: () => void;
}

export function CommentDrawer({ item, onClose }: Props) {
  const { user } = useAuth();
  const [body, setBody] = useState("");
  const [pendingDelete, setPendingDelete] = useState<Comment | null>(null);
  const { data: comments } = useComments(item?.id);
  const qc = useQueryClient();

  const createMutation = useMutation({
    mutationFn: ({ body }: { body: string }) =>
      api.createComment(item!.id, { body }),
    onSuccess: () => {
      if (!item) return;
      qc.invalidateQueries({ queryKey: ["comments", item.id] });
      qc.invalidateQueries({ queryKey: ["plan", item.planId] });
      setBody("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(commentId),
    onSuccess: () => {
      if (!item) return;
      qc.invalidateQueries({ queryKey: ["comments", item.id] });
      qc.invalidateQueries({ queryKey: ["plan", item.planId] });
    },
  });

  if (!item) return null;

  function onSubmit() {
    const trimmed = body.trim();
    if (!trimmed) return;
    createMutation.mutate({ body: trimmed });
  }

  const myId = user?.id ?? null;

  return (
    <>
      <BottomSheet
        open={!!item && !pendingDelete}
        onClose={onClose}
        title={`${item.title} · 코멘트`}
      >
        <div className="px-4 pb-4 space-y-3">
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {comments && comments.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-6">
                첫 코멘트를 남겨보세요.
              </p>
            )}
            {comments?.map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                canDelete={!!myId && c.userId === myId}
                onLongPress={() => {
                  if (c.userId === myId) setPendingDelete(c);
                }}
              />
            ))}
          </div>
          {comments && comments.some((c) => c.userId === myId) && (
            <p className="text-[11px] text-slate-400 text-center">본인 코멘트는 길게 눌러 삭제</p>
          )}

          <div className="flex gap-2 items-end">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <Button onClick={onSubmit} disabled={!body.trim() || createMutation.isPending}>
              보내기
            </Button>
          </div>
        </div>
      </BottomSheet>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        title="이 코멘트를 삭제하시겠어요?"
        message={pendingDelete ? `${pendingDelete.authorName}: ${pendingDelete.body}` : undefined}
        confirmLabel="삭제"
        destructive
      />
    </>
  );
}

function CommentRow({
  comment,
  canDelete,
  onLongPress,
}: {
  comment: Comment;
  canDelete: boolean;
  onLongPress: () => void;
}) {
  const handlers = useLongPress(onLongPress);
  return (
    <div
      {...(canDelete ? handlers : {})}
      className="bg-slate-50 rounded-xl px-3 py-2 select-none touch-manipulation"
      style={{ WebkitTouchCallout: "none" }}
    >
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-sm font-semibold text-slate-900">{comment.authorName}</span>
        <span className="text-[11px] text-slate-400 tabular-nums">
          {new Intl.DateTimeFormat([], {
            hour: "2-digit",
            minute: "2-digit",
            month: "short",
            day: "numeric",
          }).format(new Date(comment.createdAt))}
        </span>
      </div>
      <p className="text-sm text-slate-700 whitespace-pre-line break-words">{comment.body}</p>
    </div>
  );
}
